import { useState } from 'react'
import { generateTelegramCode } from '../../../services/httpService'

function TelegramGroupsCard({
	isDarkMode,
	groups,
	unlinking,
	onRemoveGroup,
	onAddError,
	onAddInitiated,
}) {
	const [deleteConfirm, setDeleteConfirm] = useState(null)
	const [deleting, setDeleting] = useState(false)
	const [adding, setAdding] = useState(false)

	const textColor = isDarkMode ? 'text-white' : 'text-gray-900'
	const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const cardBg = isDarkMode ? 'bg-gray-700' : 'bg-white'
	const borderColor = isDarkMode ? 'border-gray-600' : 'border-gray-200'

	const formatDate = dateString => {
		const date = new Date(dateString)
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})
	}

	const handleDeleteConfirm = async () => {
		if (!deleteConfirm) return
		setDeleting(true)
		await onRemoveGroup(deleteConfirm.groupId)
		setDeleting(false)
		setDeleteConfirm(null)
	}

	const handleAddGroup = async () => {
		setAdding(true)
		try {
			const code = await generateTelegramCode()
			if (!code) {
				onAddError?.("Couldn't start linking. Please try again.")
				return
			}
			onAddInitiated?.()
			window.open(
				`https://t.me/loadfetcherbot?startgroup=${code}`,
				'_blank',
				'noopener,noreferrer'
			)
		} catch (err) {
			onAddError?.(err.response?.data?.message || "Couldn't connect to Telegram. Please contact support.")
		} finally {
			setAdding(false)
		}
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
				<h2 className={`text-xl font-semibold ${textColor}`}>
					Telegram Integration
				</h2>
				<button
					onClick={handleAddGroup}
					disabled={adding}
					className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors sm:w-auto"
				>
					<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					{adding ? 'Opening Telegram…' : 'Add Telegram Group'}
				</button>
			</div>

			{groups.length === 0 ? (
				<p className={`text-xl font-semibold ${subTextColor} pt-16 pb-8 text-center`}>
					No Telegram groups connected yet
				</p>
			) : (
				<div className="space-y-2">
					{groups.map(group => (
						<div
							key={group.groupId}
							className={`${cardBg} border ${borderColor} rounded-lg px-4 py-3`}
						>
							<div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_180px_minmax(0,280px)_32px] sm:items-center sm:gap-4">
								<p className={`text-sm ${textColor} truncate`}>
									{group.groupName}
								</p>
								<p className={`text-xs ${subTextColor} whitespace-nowrap`}>
									Connected {formatDate(group.registrationDate)}
								</p>
								<div className="text-xs min-w-0 truncate" title={group.ownerEmail}>
									{group.isOwn === false ? (
										<>
											<span className={textColor}>{group.ownerName}</span>
											<span className={subTextColor}> · {group.ownerEmail}</span>
										</>
									) : (
										<>
											<span className={textColor}>You</span>
											<span className={subTextColor}> · {group.ownerEmail}</span>
										</>
									)}
								</div>
								<div className="flex justify-end">
									{group.isOwn !== false && (
										<button
											onClick={() => setDeleteConfirm(group)}
											disabled={unlinking === group.groupId || deleting}
											className={`p-1.5 rounded transition-colors ${
												isDarkMode
													? 'text-red-400 hover:bg-red-500/20'
													: 'text-red-500 hover:bg-red-50'
											} ${(unlinking === group.groupId || deleting) ? 'opacity-50 cursor-not-allowed' : ''}`}
										>
											<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
												<path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
										</button>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{deleteConfirm && (
				<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-20 p-4">
					<div className={`${cardBg} rounded-lg w-full max-w-md overflow-hidden`}>
						<div className="p-6">
							<div className="flex items-start gap-3 mb-4">
								<div className={`w-10 h-10 ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'} rounded-full flex items-center justify-center shrink-0`}>
									<svg className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
									</svg>
								</div>
								<div>
									<h3 className={`text-base font-semibold ${textColor}`}>
										Remove Telegram group
									</h3>
									<p className={`text-sm ${subTextColor} mt-1`}>
										Are you sure you want to remove <span className="font-medium">{deleteConfirm.groupName}</span>? Companies using this group will stop receiving notifications.
									</p>
								</div>
							</div>
						</div>

						<div className={`px-6 py-4 flex justify-end gap-3 ${isDarkMode ? 'border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'}`}>
							<button
								onClick={() => setDeleteConfirm(null)}
								disabled={deleting}
								className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
									isDarkMode
										? 'text-gray-300 hover:bg-gray-700'
										: 'text-gray-700 hover:bg-gray-100'
								}`}
							>
								Cancel
							</button>
							<button
								onClick={handleDeleteConfirm}
								disabled={deleting}
								className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
							>
								{deleting ? 'Removing...' : 'Remove'}
							</button>
						</div>
					</div>
				</div>
			)}

		</div>
	)
}

export default TelegramGroupsCard
